import Check from "@mui/icons-material/Check";
import Close from "@mui/icons-material/Close";
import { Chip, IconButton, Stack, Tooltip } from "@mui/material";

import { SparkleIcon } from "./SparkleIcon";
import { useSuggestionChip, UseSuggestionChipProps } from "./useSuggestionChip";

export type SuggestedChipProps = UseSuggestionChipProps & {
  label?: string;
  tooltip?: string;
  onClickChip?: () => void;
  hideAcceptButton?: boolean;
  hideRejectButton?: boolean;
  size?: "small" | "medium";
};

export function SuggestedChip({
  label = "Suggested",
  tooltip = "This field was filled in automatically using the AI tool. Please review and edit as needed.",
  onClickChip,
  hideAcceptButton,
  hideRejectButton,
  isSuggested = false,
  onAcceptSuggestion: onAcceptSuggestionProp,
  onRejectSuggestion: onRejectSuggestionProp,
  size = "small",
}: SuggestedChipProps) {
  const { showSuggestionChip, onAcceptSuggestion, onRejectSuggestion } = useSuggestionChip({
    isSuggested,
    onAcceptSuggestion: onAcceptSuggestionProp,
    onRejectSuggestion: onRejectSuggestionProp,
  });

  const handleMouseDown = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };
  if (!showSuggestionChip) {
    return null;
  }

  return (
    <Stack direction="row" alignItems="center" gap={0.5} height="1.125rem">
      <Tooltip title={tooltip}>
        <Chip
          icon={<SparkleIcon />}
          label={label}
          size={size}
          onClick={onClickChip}
          sx={{
            margin: 0.5,
            backgroundColor: (theme) =>
              theme.palette.mode === "dark" ? theme.palette.grey[500] : theme.palette.grey[100],
            color: (theme) =>
              theme.palette.mode === "dark" ? theme.palette.grey[800] : theme.palette.grey[700],
            "& .MuiChip-icon": {
              color: (theme) =>
                theme.palette.mode === "dark" ? theme.palette.grey[700] : theme.palette.grey[500],
            },
          }}
        />
      </Tooltip>
      {!hideAcceptButton && onAcceptSuggestion && (
        <Tooltip title="Accept suggestion">
          <IconButton
            size="small"
            onMouseDown={handleMouseDown}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onAcceptSuggestion(e);
            }}
            sx={{
              color: "primary.main",
              padding: 0.5,
            }}
          >
            <Check fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      {!hideRejectButton && onRejectSuggestion && (
        <Tooltip title="Reject suggestion">
          <IconButton
            size="small"
            onMouseDown={handleMouseDown}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onRejectSuggestion(e);
            }}
            sx={{
              color: "text.secondary",
              padding: 0.5,
            }}
          >
            <Close fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Stack>
  );
}
